import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { IconMicrophoneOff } from "@tabler/icons-react"
import { useEffect, useMemo, useRef, useState } from "react"
import classes from "./Visualizer.module.css"

export type TMicLevel = "silent" | "normal" | "clipping"

interface IProps {
  level: TMicLevel
  /**
   * Live RMS volume from the audio pipeline (0..1). Only used by the fallback
   * idle animation (Storybook / before monitoring); when `spectrum` is present
   * the bars are driven by real per-frequency data instead.
   */
  volume?: number
  /**
   * Real per-frequency band magnitudes (0..1) from the audio pipeline's FFT.
   * When present and monitoring, each bar samples an actual frequency band, so
   * the shape reflects what's being said (vowels light the low bars, sibilants
   * the highs). Absent (Storybook / not monitoring) → the twin-sine fallback.
   */
  spectrum?: number[]
  /** True when the audio pipeline is actively monitoring. Drives the hint copy
   * shown over flat bars; when false (no permission yet), bars stay flat. */
  monitoring?: boolean
  /** Whether to surface the "Speak into your microphone to test" quiet hint.
   * Defaults to true (the pre-record mic-test context). Set false when the
   * visualizer is reused mid-recording, where sustained quiet is normal and
   * the test-mic copy would be wrong. */
  showQuietHint?: boolean
}

const BAR_COUNT = 48
const SILENT_HINT_DELAY_MS = 1200 // bars stay flat for ≥ this long → show "speak into mic" hint

// Real-spectrum bar shaping. The pipeline hands us 0..1 band magnitudes; these
// map a magnitude to a bar height.
const BAR_FLOOR = 0.08 // never fully flat — a resting bar still reads as "alive"
const SPECTRUM_INPUT_GAIN = 1.2 // lift typical speech toward the top of the meter
const SPECTRUM_CURVE = 1.3 // > 1 expands: calm noise floor, punchy speech peaks
const CLIPPING_FLOOR = 0.7 // when clipping, keep bars visibly hot regardless of band

// Stable keys for the fixed-length bar array — position IS the identity for
// each bar, so we precompute keys instead of using the loop index in JSX.
const BAR_KEYS: readonly string[] = Array.from({ length: BAR_COUNT }, (_, n) => `viz-bar-${n}`)

export function Visualizer({
  level,
  volume = 0,
  spectrum = [],
  monitoring = true,
  showQuietHint = true,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const [phase, setPhase] = useState(0)

  // Track how long volume has been below the speech threshold so we can fade
  // the "Speak into your microphone" hint in only after a sustained quiet
  // window. This stops the hint from flashing every time someone pauses.
  // The next effect seeds the timestamp the first time volume drops below
  // threshold — that gives a true sustained-quiet window from the moment we
  // notice silence, rather than from mount.
  const quietSinceRef = useRef<number>(0)
  const [showHint, setShowHint] = useState<boolean>(monitoring && volume < 0.02)

  useEffect(() => {
    const id = setInterval(() => setPhase((p) => p + 1), 90)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    if (!monitoring) {
      setShowHint(false)
      quietSinceRef.current = 0
      return
    }
    const now = Date.now()
    if (volume < 0.02) {
      if (quietSinceRef.current === 0) quietSinceRef.current = now
      if (now - quietSinceRef.current >= SILENT_HINT_DELAY_MS) {
        setShowHint(true)
      }
    } else {
      quietSinceRef.current = 0
      setShowHint(false)
    }
  }, [monitoring, volume])

  const heights = useMemo(() => {
    // Real per-frequency render: each bar samples an actual FFT band, so the
    // shape tracks what's being said rather than a canned wave. A small floor
    // keeps it from looking dead in silence (the unprocessed mic always has a
    // little room tone, so quiet bars jitter organically); the expansion curve
    // keeps that noise floor calm while letting speech peaks shoot up.
    if (monitoring && spectrum.length > 0) {
      const lastIdx = spectrum.length - 1
      return Array.from({ length: BAR_COUNT }, (_, i) => {
        const sampled = spectrum[Math.round((i / (BAR_COUNT - 1)) * lastIdx)] ?? 0
        const shaped = Math.min(1, Math.max(0, sampled * SPECTRUM_INPUT_GAIN)) ** SPECTRUM_CURVE
        const target = BAR_FLOOR + shaped * (1 - BAR_FLOOR)
        return level === "clipping" ? Math.max(target, CLIPPING_FLOOR) : target
      })
    }

    // Fallback (no live analyser — Storybook, or idle before monitoring): a soft
    // twin-sine idle so the meter still reads as alive. Amplitude tracks RMS so
    // it isn't fully canned, but this is NOT a real spectrum — the branch above
    // is. Kept only because there's no FFT to sample here.
    const v = Math.max(0, Math.min(1, volume))
    const ceiling = level === "clipping" ? 0.95 : 0.18 + v * 1.6
    const floor = Math.max(BAR_FLOOR, ceiling * 0.25)

    return Array.from({ length: BAR_COUNT }, (_, i) => {
      const wobble = Math.sin((phase + i * 0.7) * 0.6) * 0.3 + Math.sin((phase + i * 0.3) * 1.1) * 0.18
      const target = floor + Math.abs(wobble) * (ceiling - floor)
      return Math.max(BAR_FLOOR, Math.min(1, target))
    })
  }, [phase, level, volume, spectrum, monitoring])

  const wrapperClasses = [
    classes.visualizer,
    !monitoring ? classes.silent : "",
    level === "clipping" ? classes.warning : "",
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <div
      className={wrapperClasses}
      data-testid="recording-visualizer"
      data-level={level}
      data-show-hint={showHint ? "true" : "false"}
    >
      {BAR_KEYS.map((barKey, position) => (
        <span key={barKey} className={classes.bar} style={{ transform: `scaleY(${heights[position] ?? 0.08})` }} />
      ))}
      {showHint && showQuietHint && (
        <span className={classes.hint}>
          <IconMicrophoneOff size={14} style={{ marginRight: 6 }} />
          {i18n._(msg`Speak into your microphone to test`)}
        </span>
      )}
    </div>
  )
}
